import { useState, useEffect } from 'react'
import { teacherPriorityApi, type LessonPackageResponse } from '@/services/api/teacherPriority'
import { Package, Calendar, Clock, User, CheckCircle } from 'lucide-react'

interface PackageListProps {
  requestId: string
}

export function PackageList({ requestId }: PackageListProps) {
  const [packages, setPackages] = useState<LessonPackageResponse[]>([])
  const [activePackageId, setActivePackageId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPackages = async () => {
      setIsLoading(true)
      try {
        const data = await teacherPriorityApi.getPackages(requestId)
        setPackages(data.packages)
        setError(null)
        if (!activePackageId && data.packages.length > 0) {
          const firstContacted = data.packages.find(p => p.status === 'contacted')
          setActivePackageId(firstContacted?.id || data.packages[0]?.id || null)
        }
      } catch (e: unknown) {
        setError((e as { detail?: string })?.detail || 'Packages konnten nicht geladen werden')
      } finally {
        setIsLoading(false)
      }
    }

    fetchPackages()

    const interval = setInterval(fetchPackages, 5000)

    return () => clearInterval(interval)
  }, [requestId])  // ✅ Removed activePackageId from dependencies to prevent endless loop

  if (isLoading && packages.length === 0) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    )
  }

  if (packages.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
        <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Keine Packages vorhanden</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <Package className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          Lesson Packages ({packages.length})
        </h3>
      </div>

      {packages.map((pkg) => {
        const isActive = pkg.id === activePackageId
        return (
          <div
            key={pkg.id}
            className={`relative border rounded-lg p-3 bg-white dark:bg-gray-800 transition-all duration-200 cursor-pointer ${
              isActive
                ? 'border-blue-500 shadow-xl scale-[1.02] bg-gray-50 dark:bg-gray-700 z-10'
                : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
            }`}
            onClick={() => setActivePackageId(pkg.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setActivePackageId(pkg.id)
              }
            }}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`text-sm ${
                  isActive
                    ? 'text-blue-800 font-semibold'
                    : 'font-medium text-gray-900 dark:text-gray-100'
                }`}>
                  Package #{pkg.package_rank || '—'}
                </span>
                {pkg.package_type !== 'emergency' && (
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      pkg.package_type === 'automatic'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {pkg.package_type === 'automatic' ? 'Auto' : 'Manual'}
                  </span>
                )}
                {pkg.package_type === 'emergency' && (
                  <span
                    className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                    title="Unqualifiziert an einem Panik-Tag (Nothilfe)."
                  >
                    Nothilfe
                  </span>
                )}
                {pkg.is_full_day && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                    Ganzer Tag
                  </span>
                )}
              </div>
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  pkg.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                    : pkg.status === 'contacted'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                    : pkg.status === 'assigned'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : pkg.status === 'expired'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {pkg.status === 'contacted' ? 'Kontaktiert' : pkg.status}
              </span>
            </div>

            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              {pkg.date && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {new Date(pkg.date).toLocaleDateString('de-DE', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>
                  {pkg.lesson_count || pkg.time_slot_count} von {pkg.total_lessons || '—'} Lektionen
                  {pkg.coverage_percentage !== undefined && (
                    <span className="ml-1 text-xs text-gray-500">
                      ({pkg.coverage_percentage}%)
                    </span>
                  )}
                </span>
              </div>

              {((pkg.interested_count ?? 0) > 0 || (pkg.not_interested_count ?? 0) > 0) && (
                <div className="flex items-center gap-3 text-xs ml-6">
                  {(pkg.interested_count ?? 0) > 0 && (
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <span>✓</span>
                      <span>{pkg.interested_count} interessiert</span>
                    </span>
                  )}
                  {(pkg.not_interested_count ?? 0) > 0 && (
                    <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                      <span>✗</span>
                      <span>{pkg.not_interested_count} nicht interessiert</span>
                    </span>
                  )}
                </div>
              )}

              {pkg.teacher_name && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>{pkg.teacher_name}</span>
                </div>
              )}

              {pkg.assigned_teacher_name && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Zugewiesen: {pkg.assigned_teacher_name}</span>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
